/**
 * Базы приложения помимо основной.
 *
 * Съёмные разделы держат свои данные в отдельных базах: раздел задуман так,
 * что его папку можно удалить целиком, и в схеме основной базы не должно
 * остаться ни строки. Из этого следовало неприятное: резервная копия
 * перебирала таблицы основной базы — и всё, что человек набрал в таком
 * разделе, в копию не попадало. Он переносил данные на новое устройство и
 * обнаруживал пропажу тогда, когда старого устройства уже нет.
 *
 * Поэтому копия собирает все базы приложения, а не только основную. Способ
 * общий: имена баз спрашиваются у самого хранилища, схема читается из базы и
 * едет в файл вместе с записями. Никакого знания о разделах здесь нет — и
 * появись завтра ещё один, он попадёт в копию сам.
 */

import Dexie from 'dexie';

import { record } from '@/shared/lib/diagnostics';

/** Основная база: она выгружается по таблицам и сюда не входит. */
const MAIN_DATABASE = 'kairo';

/** Свои базы отличаются по приставке: чужие базы этого браузера не наши дело. */
const PREFIX = 'kairo-';

export interface DatabaseDump {
  /**
   * Схема: имя таблицы → описание индексов в синтаксисе Dexie.
   *
   * Едет вместе с данными, потому что на новом устройстве базы может не быть
   * вовсе: раздел ни разу не открывали, и создавать её при восстановлении
   * придётся по этому описанию.
   */
  stores: Record<string, string>;
  rows: Record<string, unknown[]>;
}

export type ExtraDatabases = Record<string, DatabaseDump>;

/** Что делать с тем, что уже лежит на устройстве. */
export type RestoreMode = 'replace' | 'merge';

export interface RestoreCounts {
  added: number;
  skipped: number;
  removed: number;
}

/** Имена баз приложения, кроме основной. */
export async function listExtraDatabaseNames(): Promise<string[]> {
  const names = await Dexie.getDatabaseNames();
  return names.filter((name) => name !== MAIN_DATABASE && name.startsWith(PREFIX));
}

/** Описание индексов таблицы так, как его понимает `version().stores()`. */
function storeSpec(table: Dexie.Table): string {
  const primary = table.schema.primKey.src;
  const indexes = table.schema.indexes.map((index) => index.src);
  return [primary, ...indexes].join(', ');
}

/** Принадлежит ли запись этому человеку. Записи без владельца — общие. */
function ownedBy(row: unknown, userId: string): boolean {
  if (typeof row !== 'object' || row === null) return false;
  const owner = (row as { userId?: unknown }).userId;
  return owner === undefined || owner === userId;
}

/**
 * Выгружает все базы, кроме основной.
 *
 * Базы открываются в динамическом режиме — без описания схемы: приложение не
 * знает, что там внутри, и знать не должно. Ошибка на одной базе не отменяет
 * остальные: копия без одного раздела лучше, чем отсутствие копии.
 */
export async function dumpExtraDatabases(userId: string): Promise<ExtraDatabases> {
  const result: ExtraDatabases = {};

  for (const name of await listExtraDatabaseNames()) {
    const database = new Dexie(name);

    try {
      await database.open();

      const dump: DatabaseDump = { stores: {}, rows: {} };
      for (const table of database.tables) {
        dump.stores[table.name] = storeSpec(table);
        dump.rows[table.name] = (await table.toArray()).filter((row) => ownedBy(row, userId));
      }

      result[name] = dump;
    } catch (error) {
      // База не открылась — пропускаем её, но копию всё равно собираем.
      record('warn', `копия.${name}`, error);
    } finally {
      database.close();
    }
  }

  return result;
}

/** Сколько записей в выгрузке дополнительных баз. */
export function countExtraRecords(databases: ExtraDatabases | undefined): number {
  if (!databases) return 0;

  return Object.values(databases).reduce(
    (sum, dump) =>
      sum + Object.values(dump.rows).reduce((inner, rows) => inner + (rows?.length ?? 0), 0),
    0,
  );
}

/** Открывает базу, создавая её по описанию из копии, если её ещё нет. */
async function openForRestore(name: string, dump: DatabaseDump): Promise<Dexie> {
  const database = new Dexie(name);

  try {
    await database.open();
    return database;
  } catch {
    // Базы нет: раздел на этом устройстве ни разу не открывали. Создаём её по
    // описанию, приехавшему вместе с данными.
    database.close();

    const created = new Dexie(name);
    created.version(1).stores(dump.stores);
    await created.open();
    return created;
  }
}

/**
 * Восстанавливает дополнительные базы из копии.
 *
 * Правила те же, что и у основной: «заменить» стирает записи этого человека и
 * кладёт на их место содержимое копии, «добавить» оставляет то, что на
 * устройстве уже есть. Записи переклеиваются на текущего владельца — копия
 * могла быть снята под другим аккаунтом.
 */
export async function restoreExtraDatabases(
  databases: ExtraDatabases | undefined,
  mode: RestoreMode,
  userId: string,
): Promise<RestoreCounts> {
  const counts: RestoreCounts = { added: 0, skipped: 0, removed: 0 };
  if (!databases) return counts;

  for (const [name, dump] of Object.entries(databases)) {
    if (!name.startsWith(PREFIX)) continue;

    let database: Dexie | null = null;
    try {
      database = await openForRestore(name, dump);

      for (const [tableName, rows] of Object.entries(dump.rows)) {
        if (!Array.isArray(rows)) continue;

        const table = database.tables.find((item) => item.name === tableName);
        // Таблицы нет в этой версии раздела — данные не теряем молча, но и
        // выдумывать место для них не можем.
        if (!table) continue;

        if (mode === 'replace') {
          const own = (await table.toArray()).filter((row) => ownedBy(row, userId));
          counts.removed += own.length;
          await table.bulkDelete(own.map((row) => (row as { id: string }).id));
        }

        for (const row of rows) {
          if (typeof row !== 'object' || row === null) continue;

          const record = { ...(row as Record<string, unknown>) };
          if ('userId' in record) record.userId = userId;

          const key = record.id;
          if (mode === 'merge' && key !== undefined && (await table.get(key)) !== undefined) {
            counts.skipped += 1;
            continue;
          }

          await table.put(record);
          counts.added += 1;
        }
      }
    } catch (error) {
      // Раздел не восстановился — остальные данные копии от этого не страдают.
      // Но причина должна остаться: молча потерянный раздел выглядит как
      // «копия неполная», и разобраться в этом будет нечем.
      record('error', `копия.${name}`, error);
    } finally {
      database?.close();
    }
  }

  return counts;
}
